import {LitElement, ReactiveController, ReactiveControllerHost} from 'lit';
import PerfectScrollbar from 'perfect-scrollbar';

// TODO: Implement caching
// TODO: How does that behave when the container of the scrollbar disconnects? (not the host)

export class SpScrollbarController implements ReactiveController {
    host: ReactiveControllerHost;
    private scrollbars: Map<string, { ps: PerfectScrollbar, options?: SpScrollbarOptions }> = new Map([]);
    private counter = 0;
    private onResize = this.updateScrollbars.bind(this);

    constructor(host: ReactiveControllerHost) {
        (this.host = host).addController(this);
        window.addEventListener('resize', this.onResize);
    }

    private updateSb = (ps: PerfectScrollbar) => {
        ps.update();
    }

    /**
     *
     * @param container The container element either as reference or identified by its id
     * @param options
     */
    add(container: Element | string, options?: SpScrollbarOptions): PerfectScrollbar {
        if (typeof  container === 'string') {
            container = (this.host as LitElement).shadowRoot.getElementById(container);
        }
        const ps = new PerfectScrollbar(container, options?.psOptions);
        if (options?.identifier) {
            const existing = this.scrollbars.get(options.identifier);
            if (existing) {
                this.removeScrollbar(existing.ps, existing.options);
            }
        }
        this.scrollbars.set(options?.identifier ? options.identifier : (this.counter++).toString(), {ps, options});
        container.addEventListener('update-scrollbars', this.updateSb.bind(this, ps));
        if (options?.onScroll) {
            container.addEventListener('scroll', options.onScroll);
        }
        if (options?.endlessScroll) {
            const fct = options.endlessScroll.loadMore;
            options.endlessScroll.loadMore = e => {
                const target = e.target as HTMLElement;
                if ((target.scrollHeight - (target.scrollTop + target.offsetHeight)) < target.offsetHeight / (options.endlessScroll.loadAt ?? 4)) {
                    fct(e);
                }
            }
            container.addEventListener('scroll', options.endlessScroll.loadMore);
        }
        return ps;
    }

    async addAsync(container: Promise<Element>, options?: SpScrollbarOptions): Promise<PerfectScrollbar> {
        return this.add(await container, options);
    }

    hostConnected() {

    }

    hostDisconnected() {
        this.scrollbars.forEach(scrollbar => {
            this.removeScrollbar(scrollbar.ps, scrollbar.options);
        });
        window.removeEventListener('resize', this.onResize);
    }

    private removeScrollbar(ps: PerfectScrollbar, options?: SpScrollbarOptions) {
        ps.element.removeEventListener('update-scrollbars', this.updateSb.bind(this, ps));
        if (options?.endlessScroll) ps.element.removeEventListener('scroll', options.endlessScroll.loadMore);
        if (options?.onScroll) ps.element.removeEventListener('scroll', options.onScroll);
        ps.destroy();
    }

    private updateScrollbars() {
        this.scrollbars.forEach(scrollbar => {
            scrollbar.ps.update();
        });
    }
}

/**
 *  @property identifier When provided, scrollbars with same identifier will overwrite (and destroy) each other
 *  @property cacheMode Set to cache scroll position (identifier is needed to use cache mode). transient: only until page refresh, permanent: stored in localstorage
 *  @property endlessScroll.loadAt Provide a threshold when the loading mechanism should trigger (default is 4, meaning when the remaining scrollable height is less than 1/4 of visible height it will trigger)
 *  @property endlessScroll.loadMore Provide a function that will be triggered in order to load more content
 */
export interface SpScrollbarOptions {
    psOptions?: PerfectScrollbar.Options,
    identifier?: string;
    cacheMode?: 'transient' | 'permanent',
    onScroll?: (e: Event) => any,
    endlessScroll?: {
        loadAt?: number;
        loadMore: (e: Event) => any
    }
}

/**
 * Dispatches an event that updates all scrollbars attached to containers it passes when bubbling up the DOM
 * @param element the HTMLElement dispatching the event. If used from a LitElement, in most cases it will be "this"
 * @param awaitUpdateComplete set to true if element is a LitElement and update cycle completion should be awaited before firing event
 */
export const dispatchUpdateScrollbarsEvent = async (element: HTMLElement | Promise<HTMLElement>, awaitUpdateComplete = false) => {
    if (element instanceof Promise) {
        element = await element;
    }
    if (awaitUpdateComplete) {
        await (element as LitElement).updateComplete;
    }
    element.dispatchEvent(new Event('update-scrollbars', {bubbles: true, composed: true}));
}